/**
 * React Query hooks for Reconciliation Monitor data fetching.
 * Extends existing SOI Agent queries with file-grouping support.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useAtomValue } from 'jotai';
import {
  getUploads,
  getUploadStatus,
  getReconValues,
  rerunUpload,
  refreshMapping,
  getArgusFieldAliases,
  upsertArgusFieldAlias,
  upsertFundAlias,
  upsertCompanyAlias,
  getArgusAliases,
  getUnmatched,
  deleteArgusCompanyAlias,
  deleteArgusFundAlias,
  unmapArgusCompanyAlias,
  unmapArgusFundAlias,
  deleteArgusFieldAlias,
  ArgusAliasesResponse,
  SoiUploadsResponse,
  SoiAgentUploadDetail,
  SoiUploadListItem,
  SoiUploadListFile,
  SoiUploadStatus,
  RerunResponse,
  RefreshMappingResponse,
  MappingEdit,
  ReconValueRow,
  SoiFieldAlias,
  UpsertFieldAliasRequest,
  UpsertFundAliasRequest,
  UpsertCompanyAliasRequest,
} from '../../../../services/queries/SoiAgentQueries';
import { portcosAtomJ } from '../../../../services/state/PortCosStateJ';
import { TreeGridRow, SoiFileWithRuns } from '../types';
import { formatDate } from '../../../../util/formatters/DateFormatters';

export interface ReconCompanyOption {
  id: number | null;
  name: string;
}

const NO_AS_OF_DATE_LABEL = 'No as-of date';

function asOfDateLabelFor(period: string | null | undefined): string {
  if (!period) {
    return NO_AS_OF_DATE_LABEL;
  }
  const formatted = formatDate(period);
  return formatted || NO_AS_OF_DATE_LABEL;
}

export const RECON_FILES_KEY = 'recon-files';
export const RECON_RUN_DETAIL_KEY = 'recon-run-detail';
export const RECON_VALUES_KEY = 'recon-values';
export const RECON_FIELD_ALIASES_KEY = 'recon-field-aliases';
export const ARGUS_ALIASES_KEY = 'argus-aliases';

function calcDuration(createdAt: string | null, finishedAt: string | null, status: string): string {
  if (!createdAt) {
    return '';
  }
  if (!finishedAt) {
    // Only show a live timer for actively processing jobs
    if (status !== 'processing') {
      return '—';
    }
  }
  const start = new Date(createdAt).getTime();
  const end = finishedAt ? new Date(finishedAt).getTime() : Date.now();
  const diffMs = Math.max(0, end - start);
  if (diffMs < 1000) {
    return '';
  }
  const hrs = Math.floor(diffMs / 3600000);
  const mins = Math.floor((diffMs % 3600000) / 60000);
  const secs = Math.floor((diffMs % 60000) / 1000);
  if (hrs > 0) {
    return `${hrs}h ${mins}m`;
  }
  return `${mins}m ${secs}s`;
}

// A bundled file's status (SoiFileStatus) uses `extracting` where the
// record-level SoiUploadStatus says `processing`; map it so per-file entries
// share the board's status vocabulary. The other values are identical.
function fileStatusToUploadStatus(status: SoiUploadListFile['status']): SoiUploadStatus {
  return status === 'extracting' ? 'processing' : status;
}

export function groupUploadsByFile(uploads: SoiUploadListItem[]): SoiFileWithRuns[] {
  // A submission can bundle several files into one upload record. Expand each
  // bundled file into its own entry so a multi-file upload lists every file
  // instead of collapsing under the first file's name. The entries keep the
  // record id, so opening any of them shows the same combined reconciliation.
  const perFile: Array<SoiUploadListItem & { isBundled: boolean }> = uploads.flatMap(
    (upload): Array<SoiUploadListItem & { isBundled: boolean }> => {
      const bundled = upload.files ?? [];
      if (bundled.length <= 1) {
        return [{ ...upload, isBundled: false }];
      }
      return bundled.map((file) => ({
        ...upload,
        fileName: file.fileName,
        // No per-file fund is stored, so label each file by its own name — the
        // same rule a single-file upload of that file already follows. Strip only
        // a known spreadsheet extension so multi-dot names (e.g. `Q4.2024.xlsx`)
        // keep their meaningful part.
        fundName: file.fileName.replace(/\.(xlsx|xls|csv)$/i, ''),
        // Each chip reflects its own file's state, not the record's rolled-up
        // status: one failed file in a bundle must not tint its siblings.
        status: fileStatusToUploadStatus(file.status),
        errorMessage: file.errorMessage ?? null,
        // The record's unmatchedCount is a combined total across every bundled
        // file, not this file's own variance count — flag it so consumers don't
        // present it as per-file precision.
        isBundled: true,
      }));
    }
  );

  // Group by (asOfDate, fileName) so the same filename uploaded for different
  // as-of dates yields separate file groups under their own date bucket.
  const fileMap = new Map<string, Array<SoiUploadListItem & { isBundled: boolean }>>();

  for (const upload of perFile) {
    const key = `${upload.period ?? ''}::${upload.fileName}`;
    const runs = fileMap.get(key) || [];
    runs.push(upload);
    fileMap.set(key, runs);
  }

  const filesWithRuns: SoiFileWithRuns[] = Array.from(fileMap.values()).map((runs) => {
    const sortedRuns = runs.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    const latest = sortedRuns[0];
    const period = latest.period || null;

    return {
      fileName: latest.fileName,
      asOfDate: period,
      asOfDateLabel: asOfDateLabelFor(period),
      latestRun: {
        id: latest.id,
        // Latest run carries the highest run number, matching how `runs` below
        // numbers them (oldest = 1 … newest = count).
        runNumber: sortedRuns.length,
        fileName: latest.fileName,
        fundName: latest.fundName,
        period: latest.period,
        asOfDate: period,
        status: latest.status,
        uploadedBy: latest.createdBy || '',
        createdAt: latest.createdAt,
        finishedAt: latest.finishedAt,
        errorMessage: latest.errorMessage,
        stepCount: latest.stepCount,
        unmatchedCount: latest.unmatchedCount,
        isBundled: latest.isBundled,
      },
      runs: sortedRuns.map((run, index) => ({
        id: run.id,
        runNumber: sortedRuns.length - index, // Run 1 = oldest, Run N = most recent
        fileName: run.fileName,
        fundName: run.fundName,
        period: run.period,
        asOfDate: run.period || null,
        status: run.status,
        uploadedBy: run.createdBy || '',
        createdAt: run.createdAt,
        finishedAt: run.finishedAt,
        errorMessage: run.errorMessage,
        stepCount: run.stepCount,
        unmatchedCount: run.unmatchedCount,
        isBundled: run.isBundled,
      })),
    };
  });

  // Sort files by most recent run date (descending)
  return filesWithRuns.sort(
    (a, b) => new Date(b.latestRun.createdAt).getTime() - new Date(a.latestRun.createdAt).getTime()
  );
}

export function transformToTreeData(files: SoiFileWithRuns[]): TreeGridRow[] {
  const rows: TreeGridRow[] = [];

  for (const file of files) {
    // Only add run rows - the group headers show date/filename automatically
    for (const run of file.runs) {
      rows.push({
        path: [file.asOfDateLabel, file.fileName, `Run ${run.runNumber}`],
        isFile: false,
        id: run.id,
        fileName: file.fileName,
        asOfDate: file.asOfDate,
        asOfDateLabel: file.asOfDateLabel,
        fundName: run.fundName,
        status: run.status,
        uploadedBy: run.uploadedBy,
        createdAt: run.createdAt,
        finishedAt: run.finishedAt,
        duration: calcDuration(run.createdAt, run.finishedAt, run.status),
        runNumber: run.runNumber,
        unmatchedCount: run.unmatchedCount,
      });
    }
  }

  return rows;
}

export function useFilesQuery(autoRefresh: boolean) {
  const query = useQuery<SoiUploadsResponse>({
    queryKey: [RECON_FILES_KEY],
    queryFn: () => getUploads(undefined, 100),
    // Poll only while any upload is in a non-terminal state. Once the entire
    // list is settled, stop refetching — every 5s refetch produced a new
    // treeData reference that cascaded re-renders through ReconciliationMonitor
    // → DataReviewPanel → ag-grid, resetting any column the user had moved
    // or resized. Stops automatically when the latest run hits done/failed/
    // awaiting_alias_confirmation.
    refetchInterval: autoRefresh
      ? (q) => {
          const uploads = q.state.data?.uploads ?? [];
          const anyActive = uploads.some((u) => u.status === 'pending' || u.status === 'processing');
          return anyActive ? 5000 : false;
        }
      : false,
  });

  const files = useMemo(() => {
    if (!query.data?.uploads) {
      return [];
    }
    return groupUploadsByFile(query.data.uploads);
  }, [query.data]);

  const treeData = useMemo(() => transformToTreeData(files), [files]);

  return {
    ...query,
    files,
    treeData,
  };
}

export function useRunDetailQuery(uploadId: number | null, autoRefresh: boolean) {
  return useQuery<SoiAgentUploadDetail>({
    queryKey: [RECON_RUN_DETAIL_KEY, uploadId],
    queryFn: () => getUploadStatus(uploadId!),
    enabled: !!uploadId,
    refetchInterval: autoRefresh ? 2000 : false,
  });
}

export function useReconValuesQuery(uploadId: number | null) {
  return useQuery<ReconValueRow[]>({
    queryKey: [RECON_VALUES_KEY, uploadId],
    queryFn: () => getReconValues(uploadId!),
    enabled: !!uploadId,
  });
}

/**
 * Fetch the global ARGUS field-alias table once per recon-monitor view and
 * derive a `Map<externalName, SoiFieldAlias>` for O(1) lookup when joining
 * alias metadata onto recon-value rows. `externalName` matches exactly against
 * `reconValue.soiColumn`.
 */
export function useArgusFieldAliasesQuery() {
  const query = useQuery<SoiFieldAlias[]>({
    queryKey: [RECON_FIELD_ALIASES_KEY],
    queryFn: getArgusFieldAliases,
    staleTime: 60 * 1000,
  });

  const aliasMap = useMemo(() => {
    const map = new Map<string, SoiFieldAlias>();
    for (const row of query.data ?? []) {
      map.set(row.externalName, row);
    }
    return map;
  }, [query.data]);

  return { ...query, aliasMap };
}

/**
 * Upsert a (externalName → field) mapping. The BE flushes the parser cache so
 * subsequent runs pick up the new mapping; we invalidate both the alias list
 * and the active upload-detail query so the recon grid re-renders with the
 * new value.
 */
export function useUpsertFieldAliasMutation(uploadId: number | null) {
  const queryClient = useQueryClient();

  return useMutation<SoiFieldAlias, Error, UpsertFieldAliasRequest>({
    mutationFn: (payload) => upsertArgusFieldAlias(payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [RECON_FIELD_ALIASES_KEY] });
      if (uploadId != null) {
        await queryClient.invalidateQueries({ queryKey: [RECON_RUN_DETAIL_KEY, uploadId] });
      }
    },
  });
}

export const ARGUS_UNMATCHED_KEY = 'argus-unmatched';

/** Unresolved fund/company names for a paused upload (AWAITING flow). */
export function useUnmatchedQuery(uploadId: number | null) {
  return useQuery({
    queryKey: [ARGUS_UNMATCHED_KEY, uploadId],
    queryFn: () => getUnmatched(uploadId as number),
    enabled: uploadId != null,
  });
}

/**
 * Revoke a field alias by id. The BE flushes the parser cache on delete too;
 * mirrors useUpsertFieldAliasMutation's invalidation so the recon grid
 * re-renders instead of continuing to show the deleted mapping's stale values.
 */
export function useDeleteFieldAliasMutation(uploadId: number | null) {
  const queryClient = useQueryClient();

  return useMutation<void, Error, number>({
    mutationFn: (id) => deleteArgusFieldAlias(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [RECON_FIELD_ALIASES_KEY] });
      if (uploadId != null) {
        await queryClient.invalidateQueries({ queryKey: [RECON_RUN_DETAIL_KEY, uploadId] });
      }
    },
  });
}

/**
 * Upsert (externalName → fundId) / (externalName → companyId) mappings from the
 * inline Foresight Fund Name / Foresight Company Name cells. Mirrors
 * `useUpsertFieldAliasMutation`: invalidates the active upload detail so the
 * grid re-renders with the new resolution.
 */
export function useUpsertFundAliasMutation(uploadId: number | null) {
  const queryClient = useQueryClient();

  return useMutation<void, Error, UpsertFundAliasRequest>({
    mutationFn: (payload) => upsertFundAlias(payload),
    onSuccess: async () => {
      if (uploadId != null) {
        await queryClient.invalidateQueries({ queryKey: [RECON_RUN_DETAIL_KEY, uploadId] });
      }
    },
  });
}

export function useUpsertCompanyAliasMutation(uploadId: number | null) {
  const queryClient = useQueryClient();

  return useMutation<void, Error, UpsertCompanyAliasRequest>({
    mutationFn: (payload) => upsertCompanyAlias(payload),
    onSuccess: async () => {
      if (uploadId != null) {
        await queryClient.invalidateQueries({ queryKey: [RECON_RUN_DETAIL_KEY, uploadId] });
      }
    },
  });
}

/**
 * FD-2449 — client-wide list of user-manageable company + fund aliases (the
 * compareSOI caller↔SOI bridge rows are filtered out server-side). Backs the
 * global Aliases management tab.
 */
export function useArgusAliasesQuery() {
  return useQuery<ArgusAliasesResponse>({
    queryKey: [ARGUS_ALIASES_KEY],
    queryFn: getArgusAliases,
    staleTime: 30 * 1000,
  });
}

/**
 * Hard-delete an alias. Only invalidates the aliases list — NOT the
 * upload-scoped run-detail/unmatched views. That's correct because the sole
 * caller gates delete to already-unmapped rows, which carry no match state, so
 * the upload views stay valid. Deleting a *mapped* alias would un-match a name
 * (like unmap) and leave those views stale — if a future caller allows that,
 * give these hooks the same `uploadId` invalidation the unmap hooks have.
 */
export function useDeleteArgusCompanyAliasMutation() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, number>({
    mutationFn: (id) => deleteArgusCompanyAlias(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [ARGUS_ALIASES_KEY] });
    },
  });
}

export function useDeleteArgusFundAliasMutation() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, number>({
    mutationFn: (id) => deleteArgusFundAlias(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [ARGUS_ALIASES_KEY] });
    },
  });
}

/**
 * Unmap (soft-delete) an alias: clears its Foresight target but keeps the row
 * as an unmapped "Needs mapping" entry so it stays visible and re-mappable —
 * deleting a mapped alias should never make it vanish from the list.
 */
export function useUnmapArgusCompanyAliasMutation(uploadId: number | null) {
  const queryClient = useQueryClient();

  return useMutation<void, Error, number>({
    mutationFn: (id) => unmapArgusCompanyAlias(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [ARGUS_ALIASES_KEY] });
      // Unmap flips a matched name back to unmatched, so the upload-scoped
      // views (run detail + unmatched) must refetch or they'd keep showing the
      // name as still-mapped — mirrors the upsert mutations' invalidation.
      if (uploadId != null) {
        await queryClient.invalidateQueries({ queryKey: [RECON_RUN_DETAIL_KEY, uploadId] });
        await queryClient.invalidateQueries({ queryKey: [ARGUS_UNMATCHED_KEY, uploadId] });
      }
    },
  });
}

export function useUnmapArgusFundAliasMutation(uploadId: number | null) {
  const queryClient = useQueryClient();

  return useMutation<void, Error, number>({
    mutationFn: (id) => unmapArgusFundAlias(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [ARGUS_ALIASES_KEY] });
      if (uploadId != null) {
        await queryClient.invalidateQueries({ queryKey: [RECON_RUN_DETAIL_KEY, uploadId] });
        await queryClient.invalidateQueries({ queryKey: [ARGUS_UNMATCHED_KEY, uploadId] });
      }
    },
  });
}

/**
 * Dropdown source for the editable "Foresight Company Name" column. Primary
 * source is `portcosAtomJ` (the shared portfolio-company atom hydrated by
 * `useFetchAndSavePortCos`).
 *
 * Why a fallback: `portcosAtomJ` is populated from `/company/portfolio`, which
 * only returns companies tagged `companyType='portfolio'`. On some tenants
 * (e.g. costanoa) the SOI agent matches against `companyType='discovery'`
 * companies that never show up in the portfolio list. Without a fallback, the
 * dropdown would be empty on those tenants. We backfill from names already
 * present on the upload's `securities[].matchedCompanyName` and
 * `reconValues[].companyName`, with `id: null` (no alias-upsert support for
 * those, but at least the dropdown is usable).
 */
export function useReconCompanyOptions(detail: SoiAgentUploadDetail | undefined): ReconCompanyOption[] {
  const portcos = useAtomValue(portcosAtomJ);

  return useMemo(() => {
    const byName = new Map<string, ReconCompanyOption>();
    for (const c of portcos) {
      if (c.name) {
        byName.set(c.name, { id: c.id, name: c.name });
      }
    }

    if (byName.size === 0) {
      for (const s of detail?.results?.securities ?? []) {
        const name = s.matchedCompanyName;
        if (name && !byName.has(name)) {
          byName.set(name, { id: s.matchedCompanyId ?? null, name });
        }
      }
      for (const r of detail?.results?.reconValues ?? []) {
        const name = r.companyName;
        if (name && !byName.has(name)) {
          byName.set(name, { id: r.companyId ?? null, name });
        }
      }
    }

    return Array.from(byName.values());
  }, [portcos, detail]);
}

export function useRerunMutation() {
  const queryClient = useQueryClient();

  return useMutation<RerunResponse, Error, number>({
    mutationFn: (uploadId: number) => rerunUpload(uploadId),
    onSuccess: async () => {
      // invalidateQueries marks stale AND refetches active observers — no
      // need to follow with refetchQueries on the same key.
      await queryClient.invalidateQueries({ queryKey: [RECON_FILES_KEY] });
    },
  });
}

/**
 * Targeted single-mapping refresh — re-resolves only the rows affected by a
 * column / fund / company remap. ~100ms typical, vs ~30s for the full
 * rerun. Used by the inline mapping editor on cell save.
 */
export function useRefreshMappingMutation() {
  const queryClient = useQueryClient();

  return useMutation<RefreshMappingResponse, Error, { uploadId: number; edit: MappingEdit }>({
    mutationFn: ({ uploadId, edit }) => refreshMapping(uploadId, edit),
    onSuccess: async (_data, { uploadId }) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [RECON_FILES_KEY] }),
        queryClient.invalidateQueries({ queryKey: [RECON_RUN_DETAIL_KEY, uploadId] }),
      ]);
    },
  });
}
